<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando recibo...</p>
    </div>

    <div v-else-if="isReceipt" class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Cobranzas' }">Cobranzas</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <template v-if="pos && number">
            <span class="view-header__breadcrumb-current">
              {{ padLeft(pos, 4) }}-{{ padLeft(number, 8) }}
            </span>
          </template>
          <span v-else class="view-header__breadcrumb-current">#{{ receiptId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de recibo</h1>
            <p class="view-header__subtitle">Información detallada del recibo de cobranza.</p>
          </div>

          <div class="view-header__actions">
            <button
              type="button"
              class="btn view-header__btn view-header__btn--primary"
              @click="getPDF(receiptId)"
            >
              <i class="bi bi-download"></i>
              Reimprimir recibo
            </button>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-receipt"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">Recibo</span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-person"></i>
              </span>
              <span>Datos del cliente</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ clientName || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="number && pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de recibo
                </span>
                <span class="field-value">
                  {{ padLeft(pos, 4) }}-{{ padLeft(number, 8) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Content + totals -->
      <div class="row">
        <!-- Tables -->
        <div class="col-md-8 mb-4 mb-md-0">
          <!-- Forma de pago -->
          <div class="card items-card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-wallet2"></i>
                </span>
                <h5 class="mb-0">Forma de pago</h5>
              </div>
              <div v-if="receiptDetails.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de items: <strong>{{ receiptDetails.length }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo</th>
                      <th>Detalle</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(d, idx) in receiptDetails" :key="'rd-' + idx">
                      <td>{{ d.type }}</td>
                      <td>{{ d.detail }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(d.amount) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Comprobantes asociados -->
          <div v-if="invoices.length > 0" class="card items-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-file-earmark-check"></i>
                </span>
                <h5 class="mb-0">Comprobantes asociados</h5>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo</th>
                      <th>Numero</th>
                      <th class="text-right">Importe</th>
                      <th class="text-right">En recibo</th>
                      <th class="text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(inv, idx) in invoices" :key="'i-' + idx">
                      <td>{{ inv.type }}</td>
                      <td>{{ inv.number }}</td>
                      <td class="text-right">{{ formatAmount(inv.total) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(inv.partial) }}
                      </td>
                      <td class="text-center">
                        <a
                          :href="`/ventas/index/detalle/${inv.id}`"
                          class="btn btn-sm btn-outline-primary"
                        >
                          Ver
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Otros pagos asociados -->
          <div v-if="payments.length > 0" class="card items-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-stack"></i>
                </span>
                <h5 class="mb-0">Otros pagos asociados</h5>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Tipo</th>
                      <th>Numero</th>
                      <th class="text-right">Importe</th>
                      <th class="text-right">En recibo</th>
                      <th class="text-center">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, idx) in payments" :key="'p-' + idx">
                      <td>{{ p.type }}</td>
                      <td>{{ p.number }}</td>
                      <td class="text-right">{{ formatAmount(p.amount) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatAmount(p.partial) }}
                      </td>
                      <td class="text-center">
                        <a
                          :href="`/ventas/cobranzas/detalle-recibo/${p.cc}`"
                          class="btn btn-sm btn-outline-primary"
                        >
                          Ver
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Total del recibo</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Efectivo</span>
                <span class="value">{{ formatAmount(getDetailTotal('EFV')) }}</span>
              </div>
              <div v-if="getDetailTotal('TRA') > 0" class="total-row">
                <span class="label">Transferencia</span>
                <span class="value">{{ formatAmount(getDetailTotal('TRA')) }}</span>
              </div>
              <div class="total-row">
                <span class="label">Tarjetas</span>
                <span class="value">{{ formatAmount(getDetailTotal('CUP')) }}</span>
              </div>
              <div class="total-row">
                <span class="label">Cheques</span>
                <span class="value">{{ formatAmount(getDetailTotal('CHQ')) }}</span>
              </div>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatAmount(receipt.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="receipt.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota para el cliente</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ receipt.notes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="isWithHolding">
      <WithHoldingDetail :withholding="withholding" :invoices="invoices" />
    </div>
  </div>
</template>

<script>
import formatDateMixin from "../../mixins/formatDateMixin";
import WithHoldingDetail from './WithHolding-detail.vue';
import myaxios from 'axios';

export default {
  props: ['receiptId'],
  mixins: [formatDateMixin],
  components: {
    WithHoldingDetail,
  },
  data() {
    return {
      loading: true,
      withholding: '',
      receipt: '',
      receiptDetails: [],
      invoices: [],
      payments: [],
    };
  },
  computed: {
    pos() {
      return this.receipt.pos;
    },
    number() {
      return this.receipt.number;
    },
    clientName() {
      return this.receipt.client_name;
    },
    createdDateFormatted() {
      if (!this.receipt.created_at) return '';
      return new Date(this.receipt.created_at).toLocaleDateString('es-AR');
    },
    isReceipt() {
      return this.receipt !== '';
    },
    isWithHolding() {
      return this.withholding !== '';
    },
  },
  mounted() {
    this.getReceiptDetail();
  },
  methods: {
    getDetailTotal(type) {
      return this.receiptDetails
        .filter(d => d.type === type)
        .reduce((acc, obj) => acc + obj.amount, 0);
    },
    formatAmount(value) {
      const n = Number(value) || 0;
      return '$ ' + n.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    padLeft(number, length) {
      if (number == null) return '';
      return number.toString().padStart(length, '0');
    },
    getReceiptDetail() {
      this.loading = true;
      this.$http
        .get('/api/collections/getReceiptDetail/' + this.receiptId)
        .then(response => {
          if (response.data.success) {
            if (response.data.data.receipt !== undefined) {
              this.receipt = response.data.data.receipt;
              this.receiptDetails = response.data.data.receiptDetails;
              this.payments = response.data.data.payments;
            } else {
              this.withholding = response.data.data.withholding;
            }
            this.invoices = response.data.data.invoices;
          }
        })
        .catch(error => {
          console.error('Error al cargar recibo:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getPDF(id) {
      this.$http.get('/api/collections/reprintReceipt/' + id).then(res => {
        const uninterceptedAxiosInstance = myaxios.create();
        uninterceptedAxiosInstance
          .get(res.data.data.urlInvoice, { responseType: 'blob' })
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            const currentDate = new Date();
            const formattedDate = currentDate
              .toLocaleDateString()
              .replace(/\//g, '-');
            link.href = URL.createObjectURL(blob);
            link.download =
              'Recibo_' +
              this.receipt.number +
              '_' +
              this.receipt.client_name +
              '_' +
              formattedDate +
              '.pdf';
            link.click();
            URL.revokeObjectURL(link.href);
          });
      });
    },
  },
};
</script>
